import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  BookOpen,
  ChartBar,
  GraduationCap,
  House,
  List,
  SignOut,
  UsersThree,
} from "@phosphor-icons/react";

import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";

const roleNav = {
  learner: [
    { label: "My learning", path: "/learner/dashboard", icon: House },
    { label: "Certificates", path: "/learner/dashboard#certificates", icon: GraduationCap },
  ],
  trainer: [
    { label: "Overview", path: "/trainer/dashboard", icon: House },
    { label: "Learners", path: "/trainer/dashboard#learners", icon: UsersThree },
    { label: "Courses", path: "/trainer/dashboard#courses", icon: BookOpen },
  ],
  center_manager: [
    { label: "Center overview", path: "/center-manager/dashboard", icon: House },
    { label: "Trainers", path: "/center-manager/dashboard#trainers", icon: UsersThree },
    { label: "Batches", path: "/center-manager/dashboard#batches", icon: BookOpen },
    { label: "Attendance", path: "/center-manager/dashboard#attendance", icon: ChartBar },
  ],
  admin: [
    { label: "Courses", path: "/admin/courses", icon: BookOpen },
    { label: "Centers", path: "/admin/centers", icon: House },
    { label: "Trainers", path: "/admin/trainers", icon: UsersThree },
    { label: "Batches", path: "/admin/batches", icon: GraduationCap },
    { label: "Reports", path: "/admin/reports", icon: ChartBar },
  ],
};

export default function RoleLayout({ children, title, eyebrow }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const items = roleNav[user.role];

  const signOut = async () => {
    setIsSigningOut(true);
    await logout();
    navigate("/login");
  };

  return (
    <div className="workspace-shell" data-testid="workspace-shell">
      <aside
        className={`workspace-sidebar ${isMenuOpen ? "menu-open" : ""}`}
        data-testid="workspace-sidebar"
      >
        <div className="mobile-sidebar-heading">
          <NavLink
            className="brand-lockup"
            to={`/${user.role}/dashboard`}
            data-testid="brand-home-link"
          >
            <BrandLogo testId="workspace-brand-logo" variant="sidebar" />
          </NavLink>
          <button
            className="mobile-menu-button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            data-testid="mobile-menu-button"
          >
            <List aria-hidden="true" size={23} />
            <span>Menu</span>
          </button>
        </div>
        <div className="role-tag" data-testid="account-role-label">{user.role} portal</div>
        <nav className="workspace-nav" aria-label="Primary navigation">
          {items.map(({ label, path, icon: Icon }) => (
            <NavLink
              className="workspace-nav-link"
              key={label}
              to={path}
              onClick={() => setIsMenuOpen(false)}
              data-testid={`navigation-${label.toLowerCase().replaceAll(" ", "-")}`}
            >
              <Icon aria-hidden="true" size={20} weight="duotone" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="workspace-main">
        <header className="workspace-heading">
          <div>
            <p className="eyebrow" data-testid="page-eyebrow">{eyebrow}</p>
            <h1 data-testid="page-title">{title}</h1>
          </div>
          <div className="header-actions">
            {user.role === "center_manager" && (
              <a
                className="header-manual-link"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/lms/center-manager/user-manual`}
                data-testid="download-center-manager-manual-button"
              >
                Center Manager manual
              </a>
            )}
            <div className="header-user" data-testid="signed-in-user">
              <div className="avatar-square" aria-hidden="true">{user.name.slice(0, 1)}</div>
              <div>
                <strong data-testid="signed-in-user-name">{user.name}</strong>
                <span data-testid="signed-in-user-email">{user.email}</span>
              </div>
            </div>
            <button
              className="header-sign-out-button"
              disabled={isSigningOut}
              onClick={signOut}
              data-testid="sign-out-button"
            >
              <SignOut aria-hidden="true" size={17} />
              {isSigningOut ? "Signing out" : "Sign out"}
            </button>
            <div className="header-date" data-testid="current-workspace-date">
              {new Intl.DateTimeFormat("en", { month: "long", day: "numeric" }).format(new Date())}
            </div>
          </div>
        </header>
        {children}
      </main>
    </div>
  );
}